import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { Loader2, Save, Mic, Image as ImageIcon, Upload } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminPodcast({ onSessionExpired }) {
  const { token } = useAuth();
  const [enabled, setEnabled] = useState(true);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [embedUrl, setEmbedUrl] = useState('');
  const [coverPath, setCoverPath] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingCover, setUploadingCover] = useState(false);

  const authHeaders = { headers: { Authorization: `Bearer ${token}` }, withCredentials: true };

  const handle401 = (e) => {
    if (e.response?.status === 401) { onSessionExpired && onSessionExpired(); return true; }
    return false;
  };

  useEffect(() => {
    const fetchPodcast = async () => {
      try {
        const { data } = await axios.get(`${API}/podcast`);
        if (data) {
          setEnabled(data.enabled ?? true);
          setTitle(data.title || '');
          setDescription(data.description || '');
          setEmbedUrl(data.embed_url || '');
          setCoverPath(data.cover_path || '');
        }
      } catch (e) {
        toast.error('Error al cargar el podcast');
      } finally {
        setLoading(false);
      }
    };
    fetchPodcast();
  }, []);

  const handleCoverUpload = async (e) => {
    const f = e.target.files[0];
    if (!f) return;
    setUploadingCover(true);
    try {
      const formData = new FormData();
      formData.append('file', f);
      const { data } = await axios.post(`${API}/upload/image`, formData, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' },
        withCredentials: true,
      });
      setCoverPath(data.path);
      toast.success('Carátula subida');
    } catch (e) {
      if (!handle401(e)) toast.error('Error al subir la carátula');
    } finally {
      setUploadingCover(false);
    }
  };

  const handleSave = async () => {
    if (!title || !embedUrl) { toast.error('El título y el enlace del podcast son obligatorios'); return; }
    setSaving(true);
    try {
      await axios.put(`${API}/podcast`, {
        enabled, title, description, embed_url: embedUrl, cover_path: coverPath || null,
      }, authHeaders);
      toast.success('¡Podcast guardado correctamente!');
    } catch (e) {
      if (!handle401(e)) toast.error('Error al guardar el podcast');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center py-20"><Loader2 className="w-10 h-10 text-[#7DBDD9] animate-spin" /></div>;
  }

  const coverUrl = coverPath ? `${API}/files/${coverPath}` : null;

  return (
    <div className="max-w-2xl">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-[#4A4A4A] mb-2 flex items-center gap-2"><Mic className="w-6 h-6 text-[#7DBDD9]" />Configura tu Podcast</h2>
        <p className="text-gray-600">Incrusta tu podcast de Spotify, Apple Podcasts o YouTube. Aparecerá en una sección nueva de tu web.</p>
      </div>

      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 space-y-5">
        {/* Enable toggle */}
        <div className="flex items-center justify-between">
          <div>
            <p className="font-semibold text-[#4A4A4A]">Mostrar sección de podcast en la web</p>
            <p className="text-sm text-gray-500">Si lo desactivas, la sección no aparecerá</p>
          </div>
          <button onClick={() => setEnabled(!enabled)} data-testid="podcast-enabled-toggle"
            className={`relative w-14 h-7 rounded-full transition-colors ${enabled ? 'bg-[#A8D8EA]' : 'bg-gray-300'}`}>
            <span className={`absolute top-1 left-1 w-5 h-5 bg-white rounded-full shadow transition-transform ${enabled ? 'translate-x-7' : 'translate-x-0'}`} />
          </button>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Título del podcast *</label>
          <input type="text" value={title} onChange={(e) => setTitle(e.target.value)} data-testid="podcast-title-input"
            placeholder="Ej: Español con Cecilia" className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-[#A8D8EA] focus:ring-2 focus:ring-[#A8D8EA]/30 outline-none" />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Descripción</label>
          <textarea value={description} onChange={(e) => setDescription(e.target.value)} data-testid="podcast-description-input"
            rows={2} placeholder="Breve descripción de tu podcast" className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-[#A8D8EA] focus:ring-2 focus:ring-[#A8D8EA]/30 outline-none resize-none" />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Enlace del podcast o episodio *</label>
          <input type="text" value={embedUrl} onChange={(e) => setEmbedUrl(e.target.value)} data-testid="podcast-embed-input"
            placeholder="https://open.spotify.com/show/... o /episode/..." className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-[#A8D8EA] focus:ring-2 focus:ring-[#A8D8EA]/30 outline-none" />
          <p className="text-xs text-gray-400 mt-1">Pega el enlace normal de Spotify (programa o episodio): la web lo convierte automáticamente en reproductor. También funciona con Apple Podcasts, YouTube o iVoox.</p>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Carátula del podcast</label>
          <div className="flex items-center gap-4">
            <div className="w-24 h-24 rounded-xl bg-[#E8F5FA] flex items-center justify-center overflow-hidden flex-shrink-0 border border-gray-200">
              {coverUrl ? <img src={coverUrl} alt="Carátula" className="w-full h-full object-cover" /> : <ImageIcon className="w-8 h-8 text-[#A8D8EA]" />}
            </div>
            <label className="cursor-pointer px-4 py-2.5 bg-[#E8F5FA] text-[#7DBDD9] rounded-xl font-semibold text-sm hover:bg-[#C8E7F2] transition-colors flex items-center gap-2" data-testid="podcast-cover-label">
              {uploadingCover ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
              {uploadingCover ? 'Subiendo...' : 'Subir carátula'}
              <input type="file" accept="image/*" onChange={handleCoverUpload} data-testid="podcast-cover-input" className="hidden" />
            </label>
          </div>
        </div>

        <button onClick={handleSave} disabled={saving} data-testid="save-podcast-btn"
          className="w-full py-3.5 bg-gradient-to-r from-[#A8D8EA] to-[#7DBDD9] text-white rounded-xl font-bold shadow-lg hover:shadow-xl transition-all disabled:opacity-60 flex items-center justify-center gap-2">
          {saving ? <><Loader2 className="w-5 h-5 animate-spin" />Guardando...</> : <><Save className="w-5 h-5" />Guardar Podcast</>}
        </button>
      </div>
    </div>
  );
}
